import type { AxiosResponse } from 'axios'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { createContext, useContext, useEffect, useState } from 'react'
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { OfferInvalidMessage } from '@/components/OfferInvalidMessage'
import { useGetOfferValid } from '@/hooks/useOffer'
import { Container } from '../../../components/Container'
import { Loading } from '../../../components/Loading'
import { MainContent } from '../../../components/MainContent'
import { Section } from '../../../components/Section'
import { Steps } from '../../../components/Steps'
import WizardForbiddenMessage from '../../../components/WizardForbiddenMessage'
import { Recap } from '../../../containers/carkulka/Recap'
import { RecapExtraInsurance } from '../../../containers/carkulka/RecapExtraInsurance.1'
import { insurerOfferContext, WithOffer } from '../../../containers/hoc/WithOffer'
import { InsuranceInfo } from '../../../containers/InsuranceInfo/InsuranceInfo'
import { MeetingRecordForm } from '../../../containers/MeetingRecordForm'
import { addonOffersContext, AddonOffersProvider } from '../../../context/OfferAddonContext'
import { StepsContent } from '../../../context/StepsContext'
import type { OkAppsMeetingRecord } from '../../../dtos/OkAppsMeetingRecord'
import { useGetMeetingRecord } from '../../../hooks/useWizard'
import { insuranceParamsContext } from '../../../layouts/Wizard'
import { CompletedPage } from './completed'

type MeetingRecordCtxProps = {
  meetingRecord?: OkAppsMeetingRecord
  isLoading: boolean
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<AxiosResponse<OkAppsMeetingRecord, any>, any>>
}

export const meetingRecordContext = createContext<MeetingRecordCtxProps>({} as MeetingRecordCtxProps)

const Step5WrapperComponent = () => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const { offer } = useContext(insurerOfferContext)

  if (!insuranceParams) return null

  return (
    <AddonOffersProvider insuranceParams={insuranceParams as InsuranceParamsDto} offer={offer}>
      <Step5Component />
    </AddonOffersProvider>
  )
}

const Step5Component = () => {
  const navigate = useNavigate()
  const { insuranceParams, insuranceParamsId, isLoadingData } = useContext(insuranceParamsContext)
  const { refetch: refetchAddonOffers, addonOffers } = useContext(addonOffersContext)
  const { refetch: checkOfferValid } = useGetOfferValid(insuranceParams!.generalParams.selectedOfferId!, { enabled: false })

  const isContracted = !!insuranceParams?.generalParams.contractNumber

  // get formulare
  const { isLoading, isFetching, data, refetch } = useGetMeetingRecord(insuranceParamsId!, { enabled: false, gcTime: 0 })

  if (!isLoadingData && insuranceParams && (insuranceParams.generalParams?.lastWizardStep ?? 1) < 5) {
    navigate('/carkulka/wizard/too-far', { replace: true })
  }

  // vynuceni nacteni dat
  useEffect(() => {
    if (insuranceParamsId) {
      refetch()
      refetchAddonOffers()
    }
  }, [insuranceParamsId])

  const offerId = insuranceParams?.generalParams.selectedOfferId

  const [offerInvalid, setOfferInvalid] = useState(false)
  useEffect(() => {
    if (offerId) {
      checkOfferValid().then(({ data }) => {
        setOfferInvalid(!data)
      })

      refetch()
    }
  }, [offerId])

  return (
    <Section>
      <Container>
        <meetingRecordContext.Provider
          value={{
            isLoading: isFetching || isLoading,
            refetch: refetch,
            meetingRecord: data?.data
          }}>

          {isLoadingData && <Loading large={true} />}

          {!isContracted && !!insuranceParams && !isLoadingData &&
            <StepsContent step={5}>
              <Steps icon="car" />

              <InsuranceInfo />

              <MainContent>
                {offerInvalid && <OfferInvalidMessage url={`/carkulka/wizard/3/${insuranceParamsId}`} />}

                {!offerInvalid &&
                  <>
                    <Recap />

                    {addonOffers.length > 0 && <RecapExtraInsurance />}

                    <MeetingRecordForm />
                  </>
                }
              </MainContent>
            </StepsContent>
          }

          {isContracted && !!insuranceParams && <CompletedPage />}
        </meetingRecordContext.Provider>

        <WizardForbiddenMessage />
      </Container>
    </Section >
  )
}

export const Step5 = WithOffer(Step5WrapperComponent)